/**
 * users-adapter.ts — 用户域真实 DTO → mock 富集行 UserRow 的塑形纯函数（ADM-USR-S006）
 *
 * 接真改造：真实后端返回 snake_case 规范化 DTO（UserListItem，见
 * packages/shared/src/schemas/users.ts），而 users.jsx 页面消费的是 mock camelCase 富集行
 * UserRow（见 ~/data/mock/types.ts）。本文件做单向塑形 + 缺字段补默认，页面 0 改动、设计 100% 还原。
 * 纯函数 + 严格类型，便于单测。
 *
 * 字段映射（DTO → UserRow）：
 *   user_id                          → id
 *   display_user_id                   → displayUserId（权威展示 ID；旧响应缺失/非字符串/空白时保留空串，绝不回退 UUID）
 *   display_name ?? user_id          → name（缺昵称用 user_id 兜底，页面取 name[0] 作头像字母）
 *   email ?? ''                      → email
 *   app_id ?? ''                     → appId
 *   country ?? ''                    → country（页面用 fmtCountry 展示）
 *   language（字符串才取）?? ''        → lang（当前内容语言，A3 §5.2 = users.last_seen_language；
 *                                       null 从未上报 / 缺键（旧 server）/ 非字符串 → 空串，页面显 —）
 *   coin_balance(string) → Number    → coin（COIN 余额不会溢出 number 安全区）
 *   bonus_balance(string) → Number   → bonus
 *   total_spend(per-currency 数组)   → totalSpend（catch-up #130：fmtSpendByCurrency 转展示串「€128.40 · zł45.00」；无充值/null → '—'。页面直接渲染 {totalSpend}，不再硬编码 € 前缀）
 *   status + risk_status             → status（中文：已封禁 / 风控观察 / 正常）—— 见 toUserStatusZh
 *   risk_reason                      → risk（风控命中原因；无则 null）
 *   created_at                       → registeredAt（注册时间，ISO 8601 原样）
 *   last_login_at ?? ''              → lastSeen（最近活跃；无则空串）
 *
 * 缺字段处理（真 DTO 无、mock 有）：
 *   - appName：真 DTO 只给 app_id（无 App 名）→ 用 opts.appName（调用方按 apps 列表 / 顶栏
 *     app-scope 注入）；缺省退回 app_id（页面 chip 至少有值，不空白）。
 *   - plan：C 端无统一订阅等级事实源（订阅在 Adapty / 支付域，未进本列表口径）→ 用 opts.plan
 *     注入；缺省空串（页面 chip 展示空 plan 不崩）。
 */

import type { UserListItem } from '@novel-admin/shared';
import type { UserRow } from '~/data/mock/types';
import { fmtSpendByCurrency } from '~/lib/format';

/** 账号状态（admin 扩展表）→ 原型中文状态。封禁优先于风控；风控状态 FLAGGED/RESTRICTED 归「风控观察」。 */
function toUserStatusZh(
  status: UserListItem['status'],
  riskStatus: UserListItem['risk_status'],
): string {
  if (status === 'BANNED') return '已封禁';
  if (riskStatus === 'FLAGGED' || riskStatus === 'RESTRICTED') return '风控观察';
  return '正常';
}

/** adapter 可选注入项：真 DTO 缺失但页面需要的富集字段，由调用方（hook / apps 列表）补齐。 */
export type UserAdapterOptions = {
  /** 归属 App 显示名（真 DTO 无 app 名）：调用方按 app_id 查 apps 列表注入；缺省退回 app_id。 */
  appName?: string;
  /** 订阅等级（本列表口径无订阅事实源）：调用方注入；缺省空串。 */
  plan?: string;
};

/** 列表项 → UserRow。金额字段：coin/bonus 转 number（页面 toLocaleString）；totalSpend EUR 分 /100 转 2 位小数 string。 */
export function adaptUserListItem(item: UserListItem, opts: UserAdapterOptions = {}): UserRow {
  const displayUserId =
    typeof item.display_user_id === 'string' && item.display_user_id.trim().length > 0
      ? item.display_user_id
      : '';
  return {
    id: item.user_id,
    displayUserId,
    name: item.display_name ?? item.user_id,
    email: item.email ?? '',
    appId: item.app_id ?? '',
    appName: opts.appName ?? item.app_id ?? '',
    country: item.country ?? '',
    lang: typeof item.language === 'string' ? item.language : '',
    registeredAt: item.created_at,
    lastSeen: item.last_login_at ?? '',
    coin: Number(item.coin_balance),
    bonus: Number(item.bonus_balance),
    totalSpend: fmtSpendByCurrency(item.total_spend),
    status: toUserStatusZh(item.status, item.risk_status),
    risk: item.risk_reason,
    plan: opts.plan ?? '',
  };
}
